import React, { useCallback, useContext, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Edit, X, Banknote } from 'lucide-react';
import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import { financialEntryApi } from '../api';
import { paymentMethodLabel } from '../../../../utils/paymentMethodLabels';
import {
  FINANCIAL_ENTRY_STATUS,
  FINANCIAL_ENTRY_TYPE,
  getCategoryLabel,
  getRecurrenceLabel,
  getSubcategoryLabel,
  getTypeFormLabel,
} from '../constants';
import { getInstallmentDisplayLabel } from '../mappers';
import {
  formatCalendarDateBR,
  formatDateTimeBR,
} from '../financialFormatters';
import { FinancialEntryDetail } from '../types';
import { financialEntryRoutes } from '../routes';
import StatusBadge from '../FinancialEntriesTable/StatusBadge';
import {
  ActionsDiv,
  CloseButton,
  Container,
  ErrorMessage,
  HeaderDiv,
  HeaderText,
  InfoField,
  InfoGrid,
  InfoLabel,
  InfoValue,
  LoadingMessage,
  OutlineButton,
  Overlay,
  PrimaryButton,
  SectionTitle,
  Separator,
  TableBody,
  TableHeader,
  TableRow,
  TextButton,
} from './style';

interface ViewFinancialEntryModalProps {
  entryId: number;
  onClose: () => void;
  onUpdated: () => void;
}

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatDate = formatDateTimeBR;

const formatPercent = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return `${value}%`;
};

const ViewFinancialEntryModal: React.FC<ViewFinancialEntryModalProps> = ({
  entryId,
  onClose,
  onUpdated,
}) => {
  const navigate = useNavigate();
  const { setFeedback } = useContext(FeedbackContext);
  const [entry, setEntry] = useState<FinancialEntryDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);

  const loadEntry = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const detail = await financialEntryApi.getById(entryId);
      setEntry(detail);
    } catch {
      setError('Não foi possível carregar o lançamento.');
      setEntry(null);
    } finally {
      setLoading(false);
    }
  }, [entryId]);

  useEffect(() => {
    loadEntry();
  }, [loadEntry]);

  const handlePay = async () => {
    if (!entry || entry.status === FINANCIAL_ENTRY_STATUS.PAID) return;

    try {
      setPaying(true);
      const updated = await financialEntryApi.update(entry.id, {
        status: FINANCIAL_ENTRY_STATUS.PAID,
      });
      setEntry(updated);
      setFeedback({
        message: 'Lançamento marcado como pago.',
        isOpen: true,
        statusOk: true,
      });
      onUpdated();
    } catch {
      setFeedback({
        message: 'Erro ao atualizar status para pago.',
        isOpen: true,
        statusOk: false,
      });
    } finally {
      setPaying(false);
    }
  };

  const handleEdit = () => {
    onClose();
    navigate(financialEntryRoutes.edit(entryId));
  };

  const isPaid = entry?.status === FINANCIAL_ENTRY_STATUS.PAID;
  const installments = entry?.installments ?? [];
  const showCommission = entry?.type !== FINANCIAL_ENTRY_TYPE.EXPENSE;
  const linkedUsersLabel =
    entry?.linkedUsers?.map((user) => user.name).filter(Boolean).join(', ') ||
    '—';

  return ReactDOM.createPortal(
    <Overlay onClick={onClose}>
      <Container onClick={(event) => event.stopPropagation()}>
        <HeaderDiv>
          <HeaderText>Lançamento #{entryId}</HeaderText>
          <CloseButton type="button" onClick={onClose} aria-label="Fechar">
            <X size={16} />
          </CloseButton>
        </HeaderDiv>

        {loading && <LoadingMessage>Carregando lançamento...</LoadingMessage>}
        {error && <ErrorMessage>{error}</ErrorMessage>}

        {!loading && !error && entry && (
          <>
            <InfoGrid>
              <InfoField>
                <InfoLabel>Status</InfoLabel>
                <InfoValue>
                  <StatusBadge status={entry.status} />
                </InfoValue>
              </InfoField>
              <InfoField>
                <InfoLabel>Registro</InfoLabel>
                <InfoValue>{formatDate(entry.createdAt)}</InfoValue>
              </InfoField>
            </InfoGrid>

            <Separator />

            <InfoGrid>
              <InfoField>
                <InfoLabel>Empresa</InfoLabel>
                <InfoValue>{entry.company?.companyName ?? '—'}</InfoValue>
              </InfoField>
              <InfoField>
                <InfoLabel>Usuário vinculado</InfoLabel>
                <InfoValue>{linkedUsersLabel}</InfoValue>
              </InfoField>
              <InfoField>
                <InfoLabel>Tipo</InfoLabel>
                <InfoValue>{getTypeFormLabel(entry.type)}</InfoValue>
              </InfoField>
              <InfoField>
                <InfoLabel>Categoria</InfoLabel>
                <InfoValue>{getCategoryLabel(entry.category)}</InfoValue>
              </InfoField>
              <InfoField>
                <InfoLabel>Subcategoria</InfoLabel>
                <InfoValue>{getSubcategoryLabel(entry.subcategory)}</InfoValue>
              </InfoField>
              <InfoField>
                <InfoLabel>Valor total</InfoLabel>
                <InfoValue>{formatCurrency(entry.totalValue)}</InfoValue>
              </InfoField>
              <InfoField>
                <InfoLabel>Forma de pagamento</InfoLabel>
                <InfoValue>{paymentMethodLabel(entry.paymentMethodName)}</InfoValue>
              </InfoField>
              <InfoField>
                <InfoLabel>Recorrência</InfoLabel>
                <InfoValue>{getRecurrenceLabel(entry.recurrence)}</InfoValue>
              </InfoField>
              <InfoField>
                <InfoLabel>Descrição</InfoLabel>
                <InfoValue>{entry.description?.trim() || '—'}</InfoValue>
              </InfoField>
            </InfoGrid>

            <SectionTitle>Parcelas</SectionTitle>
            <TableHeader $hideCommission={!showCommission}>
              <div>Parcela</div>
              <div>Vencimento</div>
              <div>Valor</div>
              {showCommission && <div>Comissão</div>}
            </TableHeader>
            <TableBody>
              {installments.length === 0 ? (
                <div style={{ padding: '12px', fontSize: '13px', color: '#696969' }}>
                  Nenhuma parcela cadastrada.
                </div>
              ) : (
                installments.map((installment, index) => (
                  <TableRow
                    key={installment.id ?? index}
                    $even={index % 2 === 1}
                    $hideCommission={!showCommission}
                  >
                    <div>
                      {getInstallmentDisplayLabel(
                        installment.installmentNumber,
                        index,
                        installments.length,
                      )}
                    </div>
                    <div>{formatCalendarDateBR(installment.dueDate)}</div>
                    <div>{formatCurrency(installment.value)}</div>
                    {showCommission && (
                      <div>{formatPercent(installment.commission)}</div>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>

            <ActionsDiv>
              <TextButton type="button" onClick={onClose}>
                Fechar
              </TextButton>
              <PrimaryButton
                type="button"
                onClick={handlePay}
                disabled={isPaid || paying}
              >
                <Banknote size={16} />
                {paying ? 'Salvando...' : 'Pagar'}
              </PrimaryButton>
              <OutlineButton type="button" onClick={handleEdit}>
                <Edit size={16} />
                Editar
              </OutlineButton>
            </ActionsDiv>
          </>
        )}
      </Container>
    </Overlay>,
    document.body,
  );
};

export default ViewFinancialEntryModal;
